import React, {useContext, useEffect} from "react";
import InputMask from "react-input-mask";
import {buyNowContext} from "../BuyNow";
import CountriesPropTypes from "../../prop-types/CountriesPropTypes";
import trackingScripts from "../../utils/trackingScripts";
import { formattedNextChargeDate } from "../../utils/orderHelper";
import { createOrderObject, FACEBOOK_EVENTS } from "../../utils/facebookHelper";
import {PRODUCT_CATEGORIES} from "../../../../constants";
import { useSimpleReactValidator } from "../../utils/useSimpleReactValidator";


const PaymentStage = ({ countries }) => {
  const validator = useSimpleReactValidator();
  const {
    PURCHASE_STAGES, setPurchaseStage, paymentStageState, setPaymentStageState, shippingStageState, lineItems, isProduction,
    promoCode, giftSenderInformation
  } = useContext(buyNowContext);

  const scaleLineItem = lineItems.find(li => li.product.category === PRODUCT_CATEGORIES.SCALE);
  const planLineItem = lineItems.find(li => li.product.category === PRODUCT_CATEGORIES.MEMBERSHIP);
  const selectedScale = scaleLineItem ? scaleLineItem.product : null;
  const selectedPlan = planLineItem ? planLineItem.product : null;

  // If the user is not purchasing a scale, we shouldn't offer an option to enter a billing address. In this case,
  // we are using the shipping info for the billing info.
  const billingAddressEnabled = !!selectedScale;

  const fbTrackingOrderObject = createOrderObject(selectedScale, selectedPlan);

  const submitForm = () => {
    if (validator.current.allValid()) {
      validator.current.hideMessages();
      setPurchaseStage(PURCHASE_STAGES.REVIEW);
    } else {
      validator.current.showMessages();
    }
  };

  function onCheckingSameBillingAddress() {
    setPaymentStageState({...paymentStageState, isUsingBillingAddress: !paymentStageState.isUsingBillingAddress});
    /**
     * Hide validator messages when user select `This is a gift` options,
     * and purgeFields to register the new fields include/exclude the billing address fields based on the checkbox state.
     * we will validate again when submit.
     */
    validator.current.purgeFields();
    validator.current.hideMessages();
  }

  function removeUnderscore(value) {
    value = value.replace(/_/g, '');
    setPaymentStageState({...paymentStageState, creditCardNumber: value});
  }

  function removeMaskChar() {
    let creditCardNumLength = paymentStageState.creditCardNumber.toString().length - 3;
    if (creditCardNumLength >= 14) {
      setPaymentStageState({...paymentStageState, maskChar: ''});
    }
  }

  useEffect(() => {
    trackingScripts.hotjar(isProduction, "/orders/buy-now/payment_stage");
    trackingScripts.facebook(isProduction, FACEBOOK_EVENTS.REACT_PAGE_VIEW, { page: "PaymentStage" }, "trackCustom");
    trackingScripts.facebook(isProduction, FACEBOOK_EVENTS.INITIATE_CHECKOUT, fbTrackingOrderObject, "track", shippingStageState.email);
    trackingScripts.sendGoogleAnalyticsEvent(isProduction, "begin_checkout", "ecommerce", "Initiate Checkout");
    trackingScripts.sendGoogleAnalyticsEvent(isProduction, "add_payment_info", "ecommerce", "Add Payment Info");

    if (giftSenderInformation.isGift) {
      setPaymentStageState({...paymentStageState, isUsingBillingAddress: giftSenderInformation.isGift});
    }
  }, []);

  return (
    <React.Fragment>
      <div className="col-12 offset-lg-1 col-lg-6 mt-2 mt-lg-0 mb-5 pb-5">
        <div className="col-12 mt-4 mt-lg-0 h-100 mb-lg-4 pb-lg-1 px-0">
          <div className="col-lg-11 px-0">
            <div className="row d-flex justify-content-between align-items-center mb-2">
              <p className="col-8 col-md-9 header-txt text-dark mb-0 pe-0 pb-0 mt-2 mt-lg-0">Payment information</p>
              <img src="/img/credit_cards.png" className="img-responsive col-4 col-md-3 h-100 ps-0"/>
            </div>
            <form className="row g-3 p-0 mb-2">
              <div className="col-12">
                <InputMask
                  mask="9999 9999 9999 9999"
                  type="text"
                  className="form-control form-control-inputs"
                  onBlur={() => removeMaskChar()}
                  id="creditCardNumber"
                  placeholder="Credit card number"
                  value={paymentStageState.creditCardNumber}
                  maskChar={paymentStageState.maskChar} name={'creditCardNumber'}
                  onChange={e => {
                    removeUnderscore(e.target.value)
                  }}
                />
                {validator.current.message('creditCardNumber', paymentStageState.creditCardNumber, 'required|card_num')}
              </div>
              <div className="col-12">
                <input
                  type="text"
                  className="form-control form-control-inputs"
                  id="nameOnCard"
                  value={paymentStageState.nameOnCard}
                  name={'nameOnCard'}
                  placeholder="Name on card"
                  onChange={e => {
                    setPaymentStageState({...paymentStageState, nameOnCard: e.target.value});
                  }}
                />
                {validator.current.message('nameOnCard', paymentStageState.nameOnCard, 'required')}
              </div>
              <div className="col-12">
                <div className="row g-3">
                  <div className="col-6">
                    <InputMask
                      mask="99 / 99"
                      type="text"
                      className="form-control form-control-inputs"
                      id="creditCardDate"
                      value={paymentStageState.creditCardDate}
                      name={'creditCardDate'}
                      placeholder="MM / YY"
                      onChange={e => {
                        setPaymentStageState({...paymentStageState, creditCardDate: e.target.value});
                      }}
                    />
                    {validator.current.message('creditCardDate', paymentStageState.creditCardDate, 'required|card_exp')}
                  </div>
                  <div className="col-6">
                    <input
                      type="text"
                      className="form-control form-control-inputs"
                      id="cvv"
                      value={paymentStageState.cvv}
                      name={'cvv'}
                      placeholder="CVV"
                      maxLength={4}
                      onChange={e => {
                        setPaymentStageState({...paymentStageState, cvv: e.target.value});
                      }}
                    />
                    {validator.current.message('cvv', paymentStageState.cvv, 'required')}
                  </div>
                </div>
              </div>
            </form>

            {billingAddressEnabled &&
              <React.Fragment>
                <div className="form-check d-lg-flex align-items-lg-center my-3 col-12">
                  <input
                    type="checkbox"
                    className="form-check-input me-2"
                    id="paymentCheck"
                    defaultChecked={!giftSenderInformation.isGift}
                    name="paymentAddressCheck"
                    onChange={onCheckingSameBillingAddress}
                  />
                  <label className="form-check-label plan-main-txt fw-bolder pt-lg-1" htmlFor="paymentCheck">
                    Use the same address for billing and shipping
                  </label>
                </div>

                {paymentStageState.isUsingBillingAddress &&
                  <form className="row g-3 p-0 mt-2">
                    <p className="header-txt mt-3 text-dark my-0 pb-2">Billing address</p>
                    <div className="col-12 mt-0">
                      <input
                        type="text"
                        className="form-control form-control-inputs"
                        id="paymentInputAddress"
                        name="address1"
                        value={paymentStageState.paymentInputAddress}
                        placeholder="Address 1"
                        onChange={e => {
                          setPaymentStageState({...paymentStageState, paymentInputAddress: e.target.value});
                        }}
                      />
                      {validator.current.message('address1', paymentStageState.paymentInputAddress, 'required')}
                    </div>

                    <div className="col-12">
                      <input
                        type="text"
                        className="form-control form-control-inputs"
                        id="paymentInputAddress2"
                        name="paymentInputAddress2"
                        value={paymentStageState.paymentInputAddress2}
                        placeholder="Address 2"
                        onChange={e => {
                          setPaymentStageState({...paymentStageState, paymentInputAddress2: e.target.value});
                        }}
                      />
                    </div>

                    <div className="col-12">
                      <select
                        id="paymentInputCountry"
                        className="form-control form-control-inputs"
                        placeholder="Country"
                        name="country"
                        value={paymentStageState.paymentInputCountry.code}
                        onChange={e => {
                          setPaymentStageState({...paymentStageState, paymentInputCountry: countries[e.target.value]});
                        }}
                      >
                        {Object.keys(countries).map(countryCode => {
                          const country = countries[countryCode];
                          return (
                            <option key={countryCode} value={countryCode}>{country.name}</option>
                          );
                        })}
                      </select>
                      {validator.current.message('country', paymentStageState.paymentInputCountry, 'required')}
                    </div>

                    <div className="col-12">
                      <div className="row g-3">
                        <div className="col-md-4">
                          <input
                            type="text"
                            className="form-control form-control-inputs"
                            id="paymentInputCity"
                            name="city"
                            value={paymentStageState.paymentInputCity}
                            placeholder="City"
                            onChange={e => {
                              setPaymentStageState({...paymentStageState, paymentInputCity: e.target.value});
                            }}
                          />
                          {validator.current.message('city', paymentStageState.paymentInputCity, 'required')}
                        </div>
                        <div className="col-md-4">
                          <input
                            type="text"
                            className="form-control form-control-inputs"
                            id="paymentInputState"
                            name="state"
                            value={paymentStageState.paymentInputState}
                            placeholder="State Code"
                            maxLength={2}
                            onChange={e => {
                              setPaymentStageState({...paymentStageState, paymentInputState: e.target.value});
                            }}
                          />
                          {validator.current.message('state', paymentStageState.paymentInputState, 'required')}
                        </div>
                        <div className="col-md-4">
                          <input
                            type="text"
                            className="form-control form-control-inputs"
                            id="paymentInputZip"
                            name="zipcode"
                            value={paymentStageState.paymentInputZip}
                            placeholder="Zipcode"
                            onChange={e => {
                              setPaymentStageState({...paymentStageState, paymentInputZip: e.target.value});
                            }}
                          />
                          {validator.current.message('zipcode', paymentStageState.paymentInputZip, 'required')}
                        </div>
                      </div>
                    </div>
                  </form>
                }
              </React.Fragment>
            }
          </div>
        </div>
        <div className="col-lg-11">
          <div className="d-flex justify-content-center">
            <div className="col-lg-6 d-none d-lg-inline">
              <a
                className="sub-txt align-bottom fw-bolder border-bottom-solid text-dark back-link"
                onClick={() => setPurchaseStage(PURCHASE_STAGES.SHIPPING)}
              >
                Return to shipping
              </a>
            </div>
            <div className="col-lg-6 d-flex justify-content-center p-lg-0">
              <a
                className="teal button-link next-button-txt next-step-button align-self-center text-center py-2"
                onClick={submitForm}
              >
                Next: Review order
              </a>
            </div>
          </div>
        </div>

        {selectedPlan && selectedPlan.recurringPlan &&
          <div className="col-11 position-absolute mt-3 d-lg-none text-center">
            <p className="sub-txt">
              Your subscription will automatically renew unless you cancel
              before {formattedNextChargeDate(selectedPlan, promoCode)}. Your cancellation
              will take effect at the end of your current billing cycle.
            </p>
          </div>
        }
      </div>
    </React.Fragment>
  );
};
PaymentStage.propTypes = {
  countries: CountriesPropTypes.isRequired
};

export default PaymentStage;
